import {
  SpotlightAction as MantineSpotlightAction,
  SpotlightProvider as MantineSpotlightProvider
} from "@mantine/spotlight";
import {IconSearch, IconTruck, IconUser} from "@tabler/icons-react";
import {PropsWithChildren, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  getBooleanSorter,
  personsSelector,
  personType,
  trailersSelector, useGetAllBaseEntitiesQuery,
  useGetAllPersonsQuery,
  useGetAllTrailersQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {Loader, rem, Text} from "@mantine/core";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {setMapFocusPoint} from "../../store/map/actions";
import {
  Group,
  SpotlightAction,
  useApp,
  useAuth,
  useEntitySpotlightAction,
  useOtsSpotlightAction
} from "@ots-frontend/ots-ui";

const matchesQuery = (query: string | undefined, keywords: string[]): boolean => {
  return !query || keywords.findIndex((k) => query.toLowerCase().includes(k)) >= 0
}

export const SpotlightProvider = (props: PropsWithChildren<{}>) => {
  const {children} = props;

  const [query, setQuery] = useState("");

  const { hasAccess } = useAuth()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);
  const {regionId} = useApp();

  const {entities, isLoading: isEntitiesLoading,} = useGetAllBaseEntitiesQuery({regionId: regionId})

  const selectTrailers = useMemo(() => {
    return trailersSelector();
  }, []);

  const {trailers, isTrailersLoading} = useGetAllTrailersQuery(regionId ?? skipToken, {
    pollingInterval: 30000,
    selectFromResult: (result) => ({
      trailers: selectTrailers(result, undefined),
      isTrailersLoading: result.isLoading
    })
  });

  const selectPersons = useMemo(() => {
    return personsSelector();
  }, []);

  const {persons, isPersonsLoading} = useGetAllPersonsQuery(regionId ?? skipToken, {
    pollingInterval: 30000,
    selectFromResult: (result) => ({
      persons: selectPersons(result, {personType: personType.driver}),
      isPersonsLoading: result.isLoading
    })
  });

  const {actions: otsActions} = useOtsSpotlightAction({
    query: query
  })

  const pageActions: MantineSpotlightAction[] = useMemo(() => {
    const actions: MantineSpotlightAction[] = []

    let keywords: string[] = [
      "new",
      "person",
      "driver",
      "user"
    ]

    if (hasAccess("person_create") && matchesQuery(query, keywords)) {
      actions.push({
        title: t("common.entity.person.alt.plural", "Users"),
        description: t("common.action.new", "New") + " " + t("common.entity.person.alt.plural", "Users"),
        onTrigger: () => {
          navigate("/persons/form");
        },
        keywords: [...keywords],
        icon: <IconUser/>
      })
    }

    keywords = [
      "new",
      "trailer",
    ]

    if (hasAccess("trailer_create") && matchesQuery(query, keywords)) {
      actions.push({
        title: t("common.entity.trailer.plural", "Trailers"),
        description: t("common.action.new", "New") + " " + t("common.entity.trailer", "Trailer"),
        onTrigger: () => {
          navigate("/trailers/form");
        },
        keywords: [...keywords],
        icon: <IconTruck/>
      })
    }

    keywords = [
      "new",
      "vehicle",
    ]

    if (hasAccess("vehicle_create") && matchesQuery(query, keywords)) {
      actions.push({
        title: t("common.entity.vehicle.plural", "Vehicles"),
        description: t("common.action.new", "New") + " " + t("common.entity.vehicle", "Vehicle"),
        onTrigger: () => {
          navigate("/vehicles/form");
        },
        keywords: [...keywords],
        icon: <IconTruck/>
      })
    }

    return actions

  }, [t, query, navigate, hasAccess]);

  const trailerActions: MantineSpotlightAction[] = useMemo(() => {
    if (!query) {
      return [];
    }

    const filteredTrailers = trailers.filter((t) => {
      return (
        t.displayName &&
        t.displayName.toLowerCase().includes(query.toLowerCase())
      );
    });

    return filteredTrailers.map((t) => ({
      title: t.displayName,
      description: "Trailer",
      onTrigger: () => {
        if (t.state?.inShift && t.state?.location) {
          dispatch(setMapFocusPoint({
            id: t.id,
            displayName: t.displayName,
            latLng: t.state.location
          }));
          navigate("/map");
        } else {
          navigate(`/trailers/profile?id=${t.id}`);
        }
      },
      active: t.state?.inShift
    }));

  }, [trailers, query, dispatch, navigate]);

  const personActions: MantineSpotlightAction[] = useMemo(() => {
    if (!query) {
      return [];
    }

    const filteredPersons = persons.filter((p) => {
      return (
        p.displayName &&
        p.displayName.toLowerCase().includes(query.toLowerCase())
      );
    });

    return filteredPersons.map((p) => ({
      title: p.displayName,
      description: "Driver",
      onTrigger: () => {
        if (p.state?.inShift && p.state?.location) {
          dispatch(setMapFocusPoint({
            id: p.id,
            displayName: p.displayName,
            latLng: p.state.location
          }));
          navigate("/map");
        } else {
          navigate(`/persons/profile?id=${p.id}`);
        }
      },
      active: p.state?.inShift
    }));
  }, [persons, query, dispatch, navigate]);

  const {actions: siteActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'site',
    entities: entities.sites,
    permissions: ["view", "site_view"],
    onTrigger: (e) => navigate(`/sites/profile?id=${e.id}`)
  })

  const {actions: wellActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'well',
    entities: entities.wells,
    permissions: ["view", "well_view"],
    onTrigger: (e) => navigate(`/wells/profile?id=${e.id}`)
  })

  const {actions: containerActions} = useEntitySpotlightAction({
    query: query,
    queryPrefix: 'container',
    entities: entities.containers,
    permissions: ["view", "container_view"],
    onTrigger: (e) => navigate(`/containers/profile?id=${e.id}`)
  })

  const entityActions: MantineSpotlightAction[] = useMemo(() => {
    return [
      ...siteActions,
      ...wellActions,
      ...containerActions,
    ]
  }, [
    siteActions,
    wellActions,
    containerActions,
  ]);

  const actions: MantineSpotlightAction[] = useMemo(() => {
    const combinedActions = [...otsActions, ...pageActions, ...trailerActions, ...personActions, ...entityActions];
    combinedActions.sort(getBooleanSorter("title"))
    return combinedActions
  }, [otsActions, pageActions, trailerActions, personActions, entityActions]);

  return (
    <MantineSpotlightProvider
      actions={actions}
      actionsWrapperComponent={SpotlightActionsWrapper}
      actionComponent={SpotlightAction}
      query={query}
      onQueryChange={setQuery}
      searchIcon={(isEntitiesLoading || isTrailersLoading || isPersonsLoading) ? <Loader size="1.2rem"/> : <IconSearch size="1.2rem"/>}
      searchPlaceholder={t("common.action.search") ?? undefined}
      nothingFoundMessage={t("common.results.empty") ?? undefined}
      highlightQuery={true}
      filter={(q, actions) => actions}
      shortcut={["mod + K"]}
    >
      {children}
    </MantineSpotlightProvider>
  );
};

export const SpotlightActionsWrapper = (props: PropsWithChildren<NonNullable<unknown>>) => {
  const {children} = props;
  return (
    <div>
      <Group
        position="apart"
        px={15}
        py="xs"
        sx={(theme) => ({
          borderBottom: `${rem(1)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        })}
      >
        <Text size="xs" color="dimmed">
          To search for specific entities: use the prefixes such as <Text span fw="bold" inherit>well://</Text> , <Text span fw="bold" inherit>container://</Text> , or <Text span fw="bold" inherit>site://</Text>
        </Text>
      </Group>
      {children}
    </div>
  );
}
